import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Send, Plus, Save, Trash2, RefreshCw, MessageSquare, Users, CheckSquare, Square, AlertTriangle, FileText, Edit, Paperclip, X, Link } from 'lucide-react';
import { toast } from 'sonner';
import { useLocation } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { usePropiedad } from '../contexts/PropiedadContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MensajesWhatsApp = () => {
  const location = useLocation();
  const { propiedadSeleccionada } = usePropiedad();
  const [clientes, setClientes] = useState([]);
  const [clientesSeleccionados, setClientesSeleccionados] = useState(new Set());
  const [mensaje, setMensaje] = useState('');
  const [plantillas, setPlantillas] = useState([]);
  const [mensajesEnviados, setMensajesEnviados] = useState([]);
  const [variablesDatos, setVariablesDatos] = useState({});
  const [variablesReserva, setVariablesReserva] = useState([]);
  const [plantillaSeleccionada, setPlantillaSeleccionada] = useState('');
  const [modalPlantillaAbierto, setModalPlantillaAbierto] = useState(false);
  const [modalEditarPlantillaAbierto, setModalEditarPlantillaAbierto] = useState(false);
  const [modalHistorialAbierto, setModalHistorialAbierto] = useState(false);
  const [modalConversionAbierto, setModalConversionAbierto] = useState(false);
  const [mensajeAConvertir, setMensajeAConvertir] = useState(null);
  const [nombreNuevaPlantilla, setNombreNuevaPlantilla] = useState('');
  const [plantillaAEliminar, setPlantillaAEliminar] = useState(null);
  const [plantillaEditando, setPlantillaEditando] = useState(null);
  const [nuevaPlantilla, setNuevaPlantilla] = useState({ nombre: '', contenido: '' });
  const [modoGuardado, setModoGuardado] = useState('nueva'); // 'nueva' o 'actualizar'
  const [plantillaAActualizar, setPlantillaAActualizar] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [archivoAdjunto, setArchivoAdjunto] = useState(null);
  const [subiendoAdjunto, setSubiendoAdjunto] = useState(false);
  const [reservaIdContexto, setReservaIdContexto] = useState(null); // Para guardar el ID de reserva asociada
  const [mensajeEliminar, setMensajeEliminar] = useState(null);
  const [modalAsociarMensaje, setModalAsociarMensaje] = useState(false);
  const [mensajeAsociar, setMensajeAsociar] = useState(null);
  const [reservaSeleccionadaAsociar, setReservaSeleccionadaAsociar] = useState('');
  const [reservasDisponibles, setReservasDisponibles] = useState([]);

  const cargarDatos = useCallback(async () => {
    try {
      const clientePreseleccionado = location.state?.clientePreseleccionado;
      const nombreCliente = location.state?.nombreCliente;
      const reservaId = location.state?.reservaId;
      
      // Guardar el reserva_id si viene del contexto
      if (reservaId) {
        setReservaIdContexto(reservaId);
      }
      
      // SIEMPRE filtrar por propiedad seleccionada
      const [resClientes, resPlantillas, resMensajes, resVariables, resReservas] = await Promise.all([
        axios.get(`${API}/clientes?propiedad_id=${propiedadSeleccionada?.id_propiedad}`),
        axios.get(`${API}/plantillas-mensaje?propiedad_id=${propiedadSeleccionada?.id_propiedad}`),
        axios.get(`${API}/mensajes-enviados?propiedad_id=${propiedadSeleccionada?.id_propiedad}`),
        axios.get(`${API}/variables-mensaje?propiedad_id=${propiedadSeleccionada?.id_propiedad}`),
        axios.get(`${API}/reservas?propiedad_id=${propiedadSeleccionada?.id_propiedad}`)
      ]);
      
      setReservasDisponibles(resReservas.data);
      
      // Filtrar solo clientes con teléfono de la propiedad actual
      const clientesConTelefono = resClientes.data.filter(c => 
        c.telefono && 
        c.telefono.trim() !== '' &&
        c.propiedad_id === propiedadSeleccionada.id_propiedad
      );
      
      setClientes(clientesConTelefono);
      
      if (clientePreseleccionado || nombreCliente) {
        // Intentar encontrar el cliente por diferentes criterios
        let clienteExiste = null;
        
        // 1. Buscar por ID si está disponible
        if (clientePreseleccionado) {
          clienteExiste = clientesConTelefono.find(c => c.id_cliente === clientePreseleccionado);
        }
        
        // 2. Si no se encontró por ID, buscar por nombre (case insensitive)
        if (!clienteExiste && nombreCliente) {
          clienteExiste = clientesConTelefono.find(c => 
            c.nombre.toLowerCase().trim() === nombreCliente.toLowerCase().trim()
          );
        }
        
        // 3. Buscar por nombre + apellido
        if (!clienteExiste && nombreCliente) {
          clienteExiste = clientesConTelefono.find(c => {
            const nombreCompleto = `${c.nombre} ${c.apellido || ''}`.toLowerCase().trim();
            return nombreCompleto === nombreCliente.toLowerCase().trim();
          });
        }
        
        // 4. Si no se encontró por nombre completo, buscar por nombre parcial
        if (!clienteExiste && nombreCliente) {
          clienteExiste = clientesConTelefono.find(c => {
            const nombreCompleto = `${c.nombre} ${c.apellido || ''}`.toLowerCase();
            return nombreCompleto.includes(nombreCliente.toLowerCase().trim());
          });
        }
        
        if (clienteExiste) {
          // Seleccionar SOLO el cliente encontrado
          const nombreMostrar = `${clienteExiste.nombre} ${clienteExiste.apellido || ''}`.trim();
          setClientesSeleccionados(new Set([clienteExiste.id_cliente]));
          toast.success(`✓ Cliente seleccionado: ${nombreMostrar}`);
        } else {
          toast.warning(`No se encontró cliente con nombre "${nombreCliente}"`);
          // No seleccionar ninguno por defecto
          setClientesSeleccionados(new Set());
        }
      } else {
        // Por defecto, NO seleccionar ningún cliente
        setClientesSeleccionados(new Set());
      }
      
      setPlantillas(resPlantillas.data);
      setMensajesEnviados(resMensajes.data);
      setVariablesDatos(resVariables.data.variables_datos);
      setVariablesReserva(resVariables.data.variables_reserva || []);
    } catch (error) {
      toast.error('Error al cargar datos');
    }
  }, [propiedadSeleccionada, location.state]);

  useEffect(() => {
    if (propiedadSeleccionada) {
      cargarDatos();
    }
  }, [propiedadSeleccionada, cargarDatos]);

  const toggleCliente = (id) => {
    const nuevosSeleccionados = new Set(clientesSeleccionados);
    if (nuevosSeleccionados.has(id)) {
      nuevosSeleccionados.delete(id);
    } else {
      nuevosSeleccionados.add(id);
    }
    setClientesSeleccionados(nuevosSeleccionados);
  };

  const seleccionarTodos = () => {
    const todosIds = new Set(clientes.map(c => c.id_cliente));
    setClientesSeleccionados(todosIds);
  };

  const deseleccionarTodos = () => {
    setClientesSeleccionados(new Set());
  };

  const insertarVariable = (variable) => {
    setMensaje(mensaje + `{${variable}}`);
  };

  const eliminarVariableDato = async (key) => {
    const variable = variablesDatos[key];
    if (!variable || !variable.id_dato) {
      toast.error('No se puede eliminar esta variable');
      return;
    }
    
    if (window.confirm(`¿Estás seguro de eliminar la variable "{${key}}"? Esto eliminará el dato permanentemente.`)) {
      try {
        await axios.delete(`${API}/datos/${variable.id_dato}`);
        toast.success(`Variable "{${key}}" eliminada`);
        // Actualizar la lista de variables
        const nuevasVariables = { ...variablesDatos };
        delete nuevasVariables[key];
        setVariablesDatos(nuevasVariables);
      } catch (error) {
        toast.error('Error al eliminar la variable');
        console.error(error);
      }
    }
  };

  const cargarPlantilla = (idPlantilla) => {
    const plantilla = plantillas.find(p => p.id_plantilla === idPlantilla);
    if (plantilla) {
      setMensaje(plantilla.contenido);
      setPlantillaSeleccionada(idPlantilla);
      toast.success(`Plantilla "${plantilla.nombre}" cargada`);
    }
  };

  const abrirModalGuardarPlantilla = () => {
    setModoGuardado('nueva');
    setPlantillaAActualizar('');
    setNuevaPlantilla({ nombre: '', contenido: '' });
    setModalPlantillaAbierto(true);
  };

  const guardarComoPlantilla = async () => {
    if (!mensaje.trim()) {
      toast.error('Debes escribir un mensaje');
      return;
    }

    try {
      if (modoGuardado === 'nueva') {
        // Crear nueva plantilla
        if (!nuevaPlantilla.nombre.trim()) {
          toast.error('Debes ingresar un nombre para la plantilla');
          return;
        }
        
        await axios.post(`${API}/plantillas-mensaje`, {
          nombre: nuevaPlantilla.nombre,
          contenido: mensaje,
          propiedad_id: propiedadSeleccionada?.id_propiedad
        });
        toast.success('Plantilla creada exitosamente');
      } else {
        // Actualizar plantilla existente
        if (!plantillaAActualizar) {
          toast.error('Debes seleccionar una plantilla para actualizar');
          return;
        }
        
        const plantilla = plantillas.find(p => p.id_plantilla === plantillaAActualizar);
        await axios.put(`${API}/plantillas-mensaje/${plantillaAActualizar}`, {
          nombre: plantilla.nombre,
          contenido: mensaje,
          propiedad_id: propiedadSeleccionada?.id_propiedad
        });
        toast.success('Plantilla actualizada exitosamente');
      }
      
      setModalPlantillaAbierto(false);
      setNuevaPlantilla({ nombre: '', contenido: '' });
      setModoGuardado('nueva');
      setPlantillaAActualizar('');
      cargarDatos();
    } catch (error) {
      toast.error(modoGuardado === 'nueva' ? 'Error al crear plantilla' : 'Error al actualizar plantilla');
    }
  };

  const abrirModalEditar = (plantilla) => {
    setPlantillaEditando(plantilla);
    setNuevaPlantilla({
      nombre: plantilla.nombre,
      contenido: plantilla.contenido
    });
    setModalEditarPlantillaAbierto(true);
  };

  const actualizarPlantilla = async () => {
    if (!nuevaPlantilla.nombre.trim() || !nuevaPlantilla.contenido.trim()) {
      toast.error('Debes completar nombre y contenido');
      return;
    }

    try {
      await axios.put(`${API}/plantillas-mensaje/${plantillaEditando.id_plantilla}`, {
        nombre: nuevaPlantilla.nombre,
        contenido: nuevaPlantilla.contenido,
        propiedad_id: propiedadSeleccionada?.id_propiedad
      });
      toast.success('Plantilla actualizada exitosamente');
      setModalEditarPlantillaAbierto(false);
      setPlantillaEditando(null);
      setNuevaPlantilla({ nombre: '', contenido: '' });
      cargarDatos();
    } catch (error) {
      toast.error('Error al actualizar plantilla');
    }
  };

  const eliminarPlantilla = async () => {
    if (!plantillaAEliminar) return;

    try {
      await axios.delete(`${API}/plantillas-mensaje/${plantillaAEliminar.id_plantilla}`);
      toast.success('Plantilla eliminada');
      setPlantillaAEliminar(null);
      cargarDatos();
    } catch (error) {
      toast.error('Error al eliminar plantilla');
    }
  };

  const reemplazarVariables = (texto, cliente, reserva = null) => {
    let textoFinal = texto
      .replace(/{nombre}/g, cliente.nombre || '')
      .replace(/{apellido}/g, cliente.apellido || '')
      .replace(/{telefono}/g, cliente.telefono || '')
      .replace(/{email}/g, cliente.email || '');
    
    // Si no se pasó una reserva, intentar encontrar la más próxima del cliente
    let reservaCliente = reserva;
    if (!reservaCliente && cliente.id_cliente) {
      // Buscar reservas del cliente (por nombre completo o cliente_id)
      const nombreCompleto = `${cliente.nombre} ${cliente.apellido || ''}`.trim();
      reservaCliente = reservasDisponibles.find(r => 
        r.cliente_id === cliente.id_cliente || 
        r.huesped_nombre?.toLowerCase() === nombreCompleto.toLowerCase()
      );
    }
    
    // Reemplazar variables de reserva si existe una reserva
    if (reservaCliente) {
      textoFinal = textoFinal
        .replace(/{fecha_check_in}/g, reservaCliente.fecha_check_in ? new Date(reservaCliente.fecha_check_in + 'T12:00:00').toLocaleDateString('es-CL') : '')
        .replace(/{fecha_check_out}/g, reservaCliente.fecha_check_out ? new Date(reservaCliente.fecha_check_out + 'T12:00:00').toLocaleDateString('es-CL') : '')
        .replace(/{noches}/g, reservaCliente.noches?.toString() || '')
        .replace(/{monto_bruto}/g, reservaCliente.monto_bruto ? `$${Math.round(reservaCliente.monto_bruto).toLocaleString('es-CL')}` : '')
        .replace(/{monto_neto}/g, reservaCliente.monto_neto ? `$${Math.round(reservaCliente.monto_neto).toLocaleString('es-CL')}` : '')
        .replace(/{comisiones}/g, reservaCliente.comisiones ? `$${Math.round(reservaCliente.comisiones).toLocaleString('es-CL')}` : '')
        .replace(/{canal}/g, reservaCliente.canal_nombre || '');
    } else {
      // Si no hay reserva, limpiar las variables de reserva
      textoFinal = textoFinal
        .replace(/{fecha_check_in}/g, '')
        .replace(/{fecha_check_out}/g, '')
        .replace(/{noches}/g, '')
        .replace(/{monto_bruto}/g, '')
        .replace(/{monto_neto}/g, '')
        .replace(/{comisiones}/g, '')
        .replace(/{canal}/g, '');
    }
    
    // Reemplazar variables de datos (con adjuntos si existen)
    Object.keys(variablesDatos).forEach(key => {
      const regex = new RegExp(`{${key}}`, 'g');
      const variable = variablesDatos[key];
      
      // Si la variable tiene estructura {dato, adjunto}
      if (variable && typeof variable === 'object') {
        let reemplazo = variable.dato || '';
        
        // Si tiene adjunto, agregar enlace directo sin emoji
        if (variable.adjunto) {
          const enlaceAdjunto = `${BACKEND_URL}${variable.adjunto}`;
          reemplazo += `\n${enlaceAdjunto}`;
        }
        
        textoFinal = textoFinal.replace(regex, reemplazo);
      } else {
        // Compatibilidad con formato antiguo (solo string)
        textoFinal = textoFinal.replace(regex, variable || '');
      }
    });
    
    return textoFinal;
  };

  const handleArchivoAdjunto = (e) => {
    const file = e.target.files[0];
    if (file) {
      setArchivoAdjunto(file);
    }
  };

  const eliminarAdjuntoMensaje = () => {
    setArchivoAdjunto(null);
  };

  const subirArchivoAdjunto = async () => {
    if (!archivoAdjunto) return null;

    setSubiendoAdjunto(true);
    try {
      const formData = new FormData();
      formData.append('file', archivoAdjunto);

      const response = await axios.post(`${API}/upload-adjunto`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      return {
        url: response.data.url,
        nombre: response.data.original_filename
      };
    } catch (error) {
      toast.error('Error al subir archivo adjunto');
      return null;
    } finally {
      setSubiendoAdjunto(false);
    }
  };

  const enviarMensajes = async () => {
    if (clientesSeleccionados.size === 0) {
      toast.error('Debes seleccionar al menos un cliente');
      return;
    }

    if (!mensaje.trim()) {
      toast.error('Debes escribir un mensaje');
      return;
    }

    setEnviando(true);

    try {
      // Subir archivo adjunto si existe
      let adjuntoData = null;
      if (archivoAdjunto) {
        adjuntoData = await subirArchivoAdjunto();
        if (!adjuntoData) {
          setEnviando(false);
          return; // Error al subir archivo
        }
      }

      // Preparar destinatarios
      const destinatarios = clientes
        .filter(c => clientesSeleccionados.has(c.id_cliente))
        .map(c => ({
          id_cliente: c.id_cliente,
          nombre: c.nombre,
          telefono: c.telefono
        }));

      // Obtener información de la plantilla si se seleccionó una
      const plantillaUsada = plantillaSeleccionada ? plantillas.find(p => p.id_plantilla === plantillaSeleccionada) : null;
      
      // Registrar en BD con adjunto y contexto
      await axios.post(`${API}/mensajes-enviados`, {
        contenido: mensaje,
        destinatarios: destinatarios,
        adjunto: adjuntoData?.url || null,
        adjunto_nombre: adjuntoData?.nombre || null,
        propiedad_id: propiedadSeleccionada?.id_propiedad,
        reserva_id: reservaIdContexto || null,
        plantilla_id: plantillaUsada?.id_plantilla || null,
        plantilla_nombre: plantillaUsada?.nombre || null,
        nombre_mensaje: plantillaUsada?.nombre || null  // Nombre inicial del mensaje
      });

      // Generar enlaces y abrir pestañas
      let pestanasAbiertas = 0;
      for (const cliente of destinatarios) {
        let mensajePersonalizado = reemplazarVariables(mensaje, cliente);
        
        // Si hay adjunto, agregar enlace al mensaje
        if (adjuntoData) {
          const enlaceAdjunto = `${BACKEND_URL}${adjuntoData.url}`;
          mensajePersonalizado += `\n\n📎 Archivo adjunto: ${enlaceAdjunto}`;
        }
        
        const telefono = cliente.telefono.replace(/[^0-9]/g, '');
        const enlace = `https://wa.me/${telefono}?text=${encodeURIComponent(mensajePersonalizado)}`;
        
        setTimeout(() => {
          window.open(enlace, '_blank');
        }, pestanasAbiertas * 1000); // 1 segundo entre cada pestaña
        
        pestanasAbiertas++;
      }

      const mensajeExito = adjuntoData 
        ? `Se abrirán ${pestanasAbiertas} pestañas de WhatsApp con enlace al archivo` 
        : `Se abrirán ${pestanasAbiertas} pestañas de WhatsApp`;
      toast.success(mensajeExito);
      
      // Limpiar y recargar
      setMensaje('');
      setPlantillaSeleccionada('');
      setArchivoAdjunto(null);
      cargarDatos();
    } catch (error) {
      toast.error('Error al procesar envío');
    } finally {
      setEnviando(false);
    }
  };

  const reenviarMensaje = (mensajeEnviado) => {
    setMensaje(mensajeEnviado.contenido);
    const idsClientes = new Set(mensajeEnviado.destinatarios.map(d => d.id_cliente));
    setClientesSeleccionados(idsClientes);
    toast.info('Mensaje cargado para reenvío');
  };

  const abrirModalConversion = (mensajeEnviado) => {
    setMensajeAConvertir(mensajeEnviado);
    setNombreNuevaPlantilla('');
    setModalConversionAbierto(true);
  };

  const convertirEnPlantilla = async () => {
    if (!nombreNuevaPlantilla.trim()) {
      toast.error('Debes ingresar un nombre para la plantilla');
      return;
    }

    try {
      await axios.post(
        `${API}/mensajes-enviados/${mensajeAConvertir.id_mensaje}/convertir-plantilla`,
        null,
        { params: { nombre_plantilla: nombreNuevaPlantilla, propiedad_id: propiedadSeleccionada?.id_propiedad } }
      );
      toast.success('Mensaje convertido en plantilla');
      setModalConversionAbierto(false);
      cargarDatos();
    } catch (error) {
      toast.error('Error al convertir mensaje');
    }
  };

  const formatDate = (isoDate) => {
    return new Date(isoDate).toLocaleString('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const eliminarMensajeHistorial = async () => {
    if (!mensajeEliminar) return;

    try {
      await axios.delete(`${API}/mensajes-enviados/${mensajeEliminar.id_mensaje}`);
      toast.success('Mensaje eliminado del historial');
      setMensajeEliminar(null);
      cargarDatos(); // Recargar todos los datos
    } catch (error) {
      console.error('Error al eliminar mensaje:', error);
      toast.error('Error al eliminar el mensaje');
    }
  };

  const abrirModalAsociarMensaje = (mensaje) => {
    setMensajeAsociar(mensaje);
    // Si no tiene reserva asociada, usar el valor especial
    setReservaSeleccionadaAsociar(mensaje.reserva_id || '__sin_asociar__');
    setModalAsociarMensaje(true);
  };

  const asociarMensajeAReserva = async () => {
    if (!mensajeAsociar) return;

    try {
      // Convertir el valor especial "__sin_asociar__" a null
      const reservaId = reservaSeleccionadaAsociar === '__sin_asociar__' ? null : reservaSeleccionadaAsociar;
      
      // Construir URL correctamente
      const url = reservaId 
        ? `${API}/mensajes-enviados/${mensajeAsociar.id_mensaje}/reserva?reserva_id=${reservaId}`
        : `${API}/mensajes-enviados/${mensajeAsociar.id_mensaje}/reserva`;
      
      await axios.patch(url);
      
      toast.success(reservaId ? 'Mensaje asociado a reserva' : 'Mensaje desasociado de reserva');
      setModalAsociarMensaje(false);
      setMensajeAsociar(null);
      setReservaSeleccionadaAsociar('__sin_asociar__');
      cargarDatos(); // Recargar todos los datos
    } catch (error) {
      console.error('Error al asociar mensaje:', error);
      toast.error('Error al asociar el mensaje');
    }
  };

  return (
    <div className="space-y-6 fade-in-up">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-[#0E3A53]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Mensajes WhatsApp
          </h1>
          <p className="text-[#0E3A53]/70">Envía mensajes masivos a tus clientes</p>
        </div>
        <Button
          onClick={() => setModalHistorialAbierto(true)}
          className="bg-gradient-to-r from-[#1AA1D8] to-[#0E3A53] hover:shadow-lg"
          data-testid="btn-historial"
        >
          <MessageSquare className="w-5 h-5 mr-2" />
          Ver Historial
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Editor de Mensaje */}
        <div className="lg:col-span-2 space-y-4">
          <div className="card-beach">
            <h2 className="text-2xl font-bold text-[#0E3A53] mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Componer Mensaje
            </h2>

            {/* Selector de Plantilla */}
            <div className="mb-4">
              <Label>Plantilla (opcional)</Label>
              <div className="flex gap-2">
                <Select value={plantillaSeleccionada} onValueChange={cargarPlantilla}>
                  <SelectTrigger data-testid="select-plantilla">
                    <SelectValue placeholder="Seleccionar plantilla..." />
                  </SelectTrigger>
                  <SelectContent>
                    {plantillas.map(p => (
                      <SelectItem key={p.id_plantilla} value={p.id_plantilla}>
                        {p.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  onClick={abrirModalGuardarPlantilla}
                  data-testid="btn-guardar-plantilla"
                >
                  <Save className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Variables de Cliente */}
            <div className="mb-4">
              <Label>Variables de cliente:</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => insertarVariable('nombre')}
                  data-testid="btn-var-nombre"
                  className="bg-[#1AA1D8]/10 border-[#1AA1D8]"
                >
                  {'{nombre}'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => insertarVariable('apellido')}
                  data-testid="btn-var-apellido"
                  className="bg-[#1AA1D8]/10 border-[#1AA1D8]"
                >
                  {'{apellido}'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => insertarVariable('telefono')}
                  data-testid="btn-var-telefono"
                  className="bg-[#1AA1D8]/10 border-[#1AA1D8]"
                >
                  {'{telefono}'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => insertarVariable('email')}
                  data-testid="btn-var-email"
                  className="bg-[#1AA1D8]/10 border-[#1AA1D8]"
                >
                  {'{email}'}
                </Button>
              </div>
            </div>

            {/* Variables de Reserva */}
            {variablesReserva.length > 0 && (
              <div className="mb-4">
                <Label>Variables de reserva:</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {variablesReserva.map(variable => (
                    <Button
                      key={variable}
                      size="sm"
                      variant="outline"
                      onClick={() => insertarVariable(variable)}
                      data-testid={`btn-var-${variable}`}
                      className="bg-[#F7E8C9]/50 border-[#F7E8C9] text-[#0E3A53]"
                    >
                      {`{${variable}}`}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Variables de Datos */}
            {Object.keys(variablesDatos).length > 0 && (
              <div className="mb-4">
                <Label>Variables de datos:</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {Object.keys(variablesDatos).map(key => {
                    const variable = variablesDatos[key];
                    const tieneAdjunto = variable && typeof variable === 'object' && variable.adjunto;
                    
                    return (
                      <div key={key} className="flex items-center gap-0.5 group">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => insertarVariable(key)}
                          data-testid={`btn-var-${key.toLowerCase().replace(/\s/g, '-')}`}
                          className={tieneAdjunto 
                            ? "bg-[#1AA1D8]/20 border-[#1AA1D8] font-semibold rounded-r-none border-r-0" 
                            : "bg-[#8FE0FF]/20 border-[#0E3A53] rounded-r-none border-r-0"}
                        >
                          {tieneAdjunto && <Paperclip className="w-3 h-3 mr-1" />}
                          {`{${key}}`}
                        </Button>
                        <button
                          onClick={() => eliminarVariableDato(key)}
                          className="h-8 px-1.5 border border-l-0 border-[#FF7F6A]/50 bg-[#FF7F6A]/10 hover:bg-[#FF7F6A]/30 rounded-r-md transition-colors opacity-50 group-hover:opacity-100"
                          title={`Eliminar variable {${key}}`}
                        >
                          <X className="w-3 h-3 text-[#FF7F6A]" />
                        </button>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-[#0E3A53]/60 mt-2">
                  📎 Variables con ícono incluyen enlace de descarga | ✕ Click para eliminar variable
                </p>
              </div>
            )}

            {/* Editor */}
            <div className="mb-4">
              <Label htmlFor="mensaje">Mensaje</Label>
              <textarea
                id="mensaje"
                data-testid="textarea-mensaje"
                value={mensaje}
                onChange={(e) => setMensaje(e.target.value)}
                className="w-full min-h-[200px] p-3 border-2 border-[#8FE0FF] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1AA1D8] resize-none"
                placeholder="Escribe tu mensaje aquí..."
              />
              <p className="text-xs text-[#0E3A53]/60 mt-1">
                {mensaje.length} caracteres
              </p>
            </div>

            {/* Archivo Adjunto */}
            <div className="mb-4">
              <Label>Archivo adjunto (opcional)</Label>
              <div className="space-y-2 mt-2">
                {archivoAdjunto ? (
                  <div className="flex items-center justify-between p-3 bg-[#1AA1D8]/10 rounded-lg border-2 border-[#1AA1D8]">
                    <div className="flex items-center gap-2">
                      <Paperclip className="w-5 h-5 text-[#1AA1D8]" />
                      <div>
                        <p className="text-sm font-medium text-[#0E3A53]">{archivoAdjunto.name}</p>
                        <p className="text-xs text-[#0E3A53]/60">
                          {(archivoAdjunto.size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={eliminarAdjuntoMensaje}
                      className="p-1 hover:bg-[#FF7F6A]/20 rounded"
                      data-testid="btn-eliminar-adjunto-mensaje"
                    >
                      <X className="w-5 h-5 text-[#FF7F6A]" />
                    </button>
                  </div>
                ) : (
                  <div>
                    <input
                      type="file"
                      id="adjunto-mensaje-input"
                      data-testid="input-adjunto-mensaje"
                      onChange={handleArchivoAdjunto}
                      className="hidden"
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('adjunto-mensaje-input').click()}
                      className="w-full border-[#1AA1D8] text-[#1AA1D8] hover:bg-[#1AA1D8]/10"
                      data-testid="btn-adjuntar-archivo"
                    >
                      <Paperclip className="w-4 h-4 mr-2" />
                      Adjuntar imagen o documento
                    </Button>
                  </div>
                )}
                <p className="text-xs text-[#0E3A53]/60">
                  {archivoAdjunto 
                    ? "ℹ️ Se generará un enlace de descarga en el mensaje" 
                    : "Imágenes, PDF, Word, Excel"}
                </p>
              </div>
            </div>

            {/* Vista Previa */}
            {mensaje && clientes.length > 0 && (
              <div className="mb-4 p-4 bg-[#8FE0FF]/10 rounded-lg border-2 border-[#1AA1D8]/20">
                <Label className="mb-2 block">Vista Previa (con datos del primer cliente)</Label>
                <p className="text-sm text-[#0E3A53] whitespace-pre-wrap">
                  {reemplazarVariables(mensaje, clientes[0])}
                </p>
              </div>
            )}

            {/* Botón Enviar */}
            <Button
              onClick={enviarMensajes}
              disabled={enviando || subiendoAdjunto || clientesSeleccionados.size === 0 || !mensaje.trim()}
              className="w-full bg-gradient-to-r from-[#1AA1D8] to-[#0E3A53] hover:shadow-lg"
              data-testid="btn-enviar"
            >
              <Send className="w-5 h-5 mr-2" />
              {subiendoAdjunto 
                ? 'Subiendo archivo...' 
                : enviando 
                  ? 'Preparando envío...' 
                  : `Enviar a ${clientesSeleccionados.size} cliente(s)`}
            </Button>
          </div>
        </div>

        {/* Lista de Destinatarios */}
        <div className="card-beach">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-[#0E3A53]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              <Users className="inline w-5 h-5 mr-2" />
              Destinatarios
            </h2>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={seleccionarTodos} data-testid="btn-seleccionar-todos">
                Todos
              </Button>
              <Button size="sm" variant="outline" onClick={deseleccionarTodos} data-testid="btn-deseleccionar-todos">
                Ninguno
              </Button>
            </div>
          </div>

          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {clientes.length === 0 ? (
              <p className="text-center text-[#0E3A53]/60 py-8">
                No hay clientes con teléfono registrado
              </p>
            ) : (
              clientes.map(cliente => (
                <div
                  key={cliente.id_cliente}
                  onClick={() => toggleCliente(cliente.id_cliente)}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-[#8FE0FF]/10 cursor-pointer transition-colors"
                  data-testid={`cliente-item-${cliente.id_cliente}`}
                >
                  {clientesSeleccionados.has(cliente.id_cliente) ? (
                    <CheckSquare className="w-5 h-5 text-[#1AA1D8]" />
                  ) : (
                    <Square className="w-5 h-5 text-[#0E3A53]/40" />
                  )}
                  <div className="flex-1">
                    <p className="font-medium text-[#0E3A53]">{cliente.nombre} {cliente.apellido}</p>
                    <p className="text-xs text-[#0E3A53]/60">{cliente.telefono}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Modal Guardar Plantilla */}
      <Dialog open={modalPlantillaAbierto} onOpenChange={setModalPlantillaAbierto}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#0E3A53]">Guardar Plantilla</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Selector de modo */}
            <div className="p-4 bg-[#8FE0FF]/10 rounded-lg border-2 border-[#1AA1D8]/20">
              <Label className="mb-3 block font-semibold text-[#0E3A53]">¿Qué deseas hacer?</Label>
              <div className="space-y-2">
                <div 
                  onClick={() => {
                    setModoGuardado('nueva');
                    setPlantillaAActualizar('');
                  }}
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                    modoGuardado === 'nueva' 
                      ? 'bg-[#1AA1D8]/20 border-2 border-[#1AA1D8]' 
                      : 'bg-white border-2 border-[#8FE0FF] hover:bg-[#8FE0FF]/10'
                  }`}
                  data-testid="option-nueva-plantilla"
                >
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    modoGuardado === 'nueva' ? 'border-[#1AA1D8]' : 'border-[#0E3A53]/40'
                  }`}>
                    {modoGuardado === 'nueva' && (
                      <div className="w-3 h-3 rounded-full bg-[#1AA1D8]"></div>
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-[#0E3A53]">Crear nueva plantilla</p>
                    <p className="text-xs text-[#0E3A53]/60">Guardar con un nuevo nombre</p>
                  </div>
                </div>
                
                <div 
                  onClick={() => {
                    setModoGuardado('actualizar');
                    setNuevaPlantilla({ nombre: '', contenido: '' });
                  }}
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                    modoGuardado === 'actualizar' 
                      ? 'bg-[#1AA1D8]/20 border-2 border-[#1AA1D8]' 
                      : 'bg-white border-2 border-[#8FE0FF] hover:bg-[#8FE0FF]/10'
                  }`}
                  data-testid="option-actualizar-plantilla"
                >
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    modoGuardado === 'actualizar' ? 'border-[#1AA1D8]' : 'border-[#0E3A53]/40'
                  }`}>
                    {modoGuardado === 'actualizar' && (
                      <div className="w-3 h-3 rounded-full bg-[#1AA1D8]"></div>
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-[#0E3A53]">Actualizar plantilla existente</p>
                    <p className="text-xs text-[#0E3A53]/60">Sobrescribir una plantilla</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Campo para nueva plantilla */}
            {modoGuardado === 'nueva' && (
              <div>
                <Label htmlFor="nombre-plantilla">Nombre de la nueva plantilla *</Label>
                <Input
                  id="nombre-plantilla"
                  data-testid="input-nombre-plantilla"
                  value={nuevaPlantilla.nombre}
                  onChange={(e) => setNuevaPlantilla({ ...nuevaPlantilla, nombre: e.target.value })}
                  placeholder="Ej: Bienvenida nuevos clientes"
                />
              </div>
            )}

            {/* Selector para actualizar plantilla existente */}
            {modoGuardado === 'actualizar' && (
              <div>
                <Label htmlFor="plantilla-actualizar">Selecciona plantilla a actualizar *</Label>
                <Select value={plantillaAActualizar} onValueChange={setPlantillaAActualizar}>
                  <SelectTrigger id="plantilla-actualizar" data-testid="select-plantilla-actualizar">
                    <SelectValue placeholder="Seleccionar plantilla..." />
                  </SelectTrigger>
                  <SelectContent>
                    {plantillas.map(p => (
                      <SelectItem key={p.id_plantilla} value={p.id_plantilla}>
                        {p.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {plantillaAActualizar && (
                  <p className="text-xs text-[#FF7F6A] mt-2">
                    ⚠️ Esto sobrescribirá el contenido de la plantilla seleccionada
                  </p>
                )}
              </div>
            )}

            {/* Vista previa del contenido */}
            <div>
              <Label>Contenido que se guardará</Label>
              <textarea
                value={mensaje}
                disabled
                className="w-full min-h-[100px] p-3 border-2 border-[#8FE0FF] rounded-lg bg-gray-50 resize-none text-sm"
              />
              <p className="text-xs text-[#0E3A53]/60 mt-1">
                {mensaje.length} caracteres
              </p>
            </div>
          </div>
          <div className="flex gap-3 justify-end mt-4">
            <Button 
              variant="outline" 
              onClick={() => {
                setModalPlantillaAbierto(false);
                setModoGuardado('nueva');
                setPlantillaAActualizar('');
              }}
              data-testid="btn-cancelar-guardar"
            >
              Cancelar
            </Button>
            <Button 
              onClick={guardarComoPlantilla} 
              className="bg-gradient-to-r from-[#1AA1D8] to-[#0E3A53]"
              data-testid="btn-confirmar-guardar"
            >
              {modoGuardado === 'nueva' ? 'Crear Plantilla' : 'Actualizar Plantilla'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Editar Plantilla */}
      <Dialog open={modalEditarPlantillaAbierto} onOpenChange={setModalEditarPlantillaAbierto}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#0E3A53]">Editar Plantilla</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="nombre-plantilla-editar">Nombre de la plantilla</Label>
              <Input
                id="nombre-plantilla-editar"
                data-testid="input-nombre-plantilla-editar"
                value={nuevaPlantilla.nombre}
                onChange={(e) => setNuevaPlantilla({ ...nuevaPlantilla, nombre: e.target.value })}
                placeholder="Ej: Bienvenida nuevos clientes"
              />
            </div>
            <div>
              <Label htmlFor="contenido-plantilla-editar">Contenido</Label>
              <textarea
                id="contenido-plantilla-editar"
                data-testid="textarea-contenido-plantilla-editar"
                value={nuevaPlantilla.contenido}
                onChange={(e) => setNuevaPlantilla({ ...nuevaPlantilla, contenido: e.target.value })}
                className="w-full min-h-[150px] p-3 border-2 border-[#8FE0FF] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1AA1D8] resize-none"
                placeholder="Escribe el contenido de la plantilla..."
              />
              <p className="text-xs text-[#0E3A53]/60 mt-1">
                {nuevaPlantilla.contenido.length} caracteres
              </p>
            </div>
          </div>
          <div className="flex gap-3 justify-end mt-4">
            <Button 
              variant="outline" 
              onClick={() => {
                setModalEditarPlantillaAbierto(false);
                setPlantillaEditando(null);
                setNuevaPlantilla({ nombre: '', contenido: '' });
              }}
              data-testid="btn-cancelar-editar"
            >
              Cancelar
            </Button>
            <Button 
              onClick={actualizarPlantilla} 
              className="bg-gradient-to-r from-[#1AA1D8] to-[#0E3A53]"
              data-testid="btn-guardar-edicion"
            >
              Actualizar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Historial */}
      <Dialog open={modalHistorialAbierto} onOpenChange={setModalHistorialAbierto}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#0E3A53]">Historial de Mensajes</DialogTitle>
          </DialogHeader>
          
          {/* Lista de Plantillas */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-[#0E3A53] mb-3">Plantillas Guardadas</h3>
            {plantillas.length === 0 ? (
              <p className="text-[#0E3A53]/60 text-center py-4">No hay plantillas guardadas</p>
            ) : (
              <div className="space-y-2">
                {plantillas.map(plantilla => (
                  <div key={plantilla.id_plantilla} className="p-4 bg-[#8FE0FF]/10 rounded-lg">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <h4 className="font-semibold text-[#0E3A53]">{plantilla.nombre}</h4>
                        <p className="text-sm text-[#0E3A53]/70 mt-1 whitespace-pre-wrap">{plantilla.contenido}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => abrirModalEditar(plantilla)}
                          data-testid={`btn-editar-plantilla-${plantilla.id_plantilla}`}
                          className="hover:bg-[#1AA1D8]/10"
                        >
                          <Edit className="w-4 h-4 text-[#1AA1D8]" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setPlantillaAEliminar(plantilla)}
                          data-testid={`btn-eliminar-plantilla-${plantilla.id_plantilla}`}
                          className="hover:bg-[#FF7F6A]/10"
                        >
                          <Trash2 className="w-4 h-4 text-[#FF7F6A]" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Mensajes Enviados */}
          <div>
            <h3 className="text-lg font-semibold text-[#0E3A53] mb-3">Mensajes Enviados</h3>
            {mensajesEnviados.length === 0 ? (
              <p className="text-[#0E3A53]/60 text-center py-4">No hay mensajes enviados</p>
            ) : (
              <div className="space-y-3">
                {mensajesEnviados.map(msg => (
                  <div key={msg.id_mensaje} className="p-4 bg-white rounded-lg border-2 border-[#8FE0FF]">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-[#0E3A53]/60">
                          {formatDate(msg.fecha_envio)} • {msg.total_destinatarios} destinatarios
                        </p>
                        <p className="text-sm text-[#0E3A53] mt-2 whitespace-pre-wrap">{msg.contenido}</p>
                        {msg.adjunto && (
                          <a 
                            href={`${BACKEND_URL}${msg.adjunto}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-sm text-[#1AA1D8] hover:underline mt-2"
                            data-testid={`adjunto-mensaje-${msg.id_mensaje}`}
                          >
                            <Paperclip className="w-4 h-4" />
                            {msg.adjunto_nombre || 'Ver adjunto'}
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => abrirModalAsociarMensaje(msg)}
                        data-testid={`btn-asociar-${msg.id_mensaje}`}
                        className="border-purple-300 text-purple-600 hover:bg-purple-50"
                      >
                        <Link className="w-4 h-4 mr-1" />
                        Asociar a Reserva
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => reenviarMensaje(msg)}
                        data-testid={`btn-reenviar-${msg.id_mensaje}`}
                      >
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Reenviar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => abrirModalConversion(msg)}
                        data-testid={`btn-convertir-${msg.id_mensaje}`}
                      >
                        <FileText className="w-4 h-4 mr-1" />
                        Convertir en Plantilla
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setMensajeEliminar(msg)}
                        data-testid={`btn-eliminar-${msg.id_mensaje}`}
                        className="border-red-300 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Eliminar
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Convertir en Plantilla */}
      <Dialog open={modalConversionAbierto} onOpenChange={setModalConversionAbierto}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#0E3A53]">Convertir en Plantilla</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="nombre-nueva-plantilla">Nombre de la plantilla</Label>
              <Input
                id="nombre-nueva-plantilla"
                data-testid="input-nombre-conversion"
                value={nombreNuevaPlantilla}
                onChange={(e) => setNombreNuevaPlantilla(e.target.value)}
                placeholder="Ej: Promoción verano"
              />
            </div>
            {mensajeAConvertir && (
              <div>
                <Label>Contenido</Label>
                <div className="p-3 bg-[#8FE0FF]/10 rounded-lg">
                  <p className="text-sm text-[#0E3A53] whitespace-pre-wrap">{mensajeAConvertir.contenido}</p>
                </div>
              </div>
            )}
          </div>
          <div className="flex gap-3 justify-end mt-4">
            <Button variant="outline" onClick={() => setModalConversionAbierto(false)}>
              Cancelar
            </Button>
            <Button onClick={convertirEnPlantilla} className="bg-gradient-to-r from-[#1AA1D8] to-[#0E3A53]">
              Crear Plantilla
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Alert Dialog Eliminar Plantilla */}
      <AlertDialog open={!!plantillaAEliminar} onOpenChange={() => setPlantillaAEliminar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-[#FF7F6A]">
              <AlertTriangle className="w-6 h-6" />
              ¿Eliminar esta plantilla?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente la plantilla:
              {plantillaAEliminar && (
                <div className="mt-2 p-3 bg-[#F7E8C9] rounded-lg">
                  <p className="font-semibold text-[#0E3A53]">{plantillaAEliminar.nombre}</p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={eliminarPlantilla}
              className="bg-[#FF7F6A] hover:bg-[#FF7F6A]/90"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal de confirmación para eliminar mensaje */}
      <AlertDialog open={!!mensajeEliminar} onOpenChange={() => setMensajeEliminar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar mensaje del historial?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. El mensaje se eliminará permanentemente del historial.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={eliminarMensajeHistorial}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal para asociar mensaje a reserva */}
      <Dialog open={modalAsociarMensaje} onOpenChange={setModalAsociarMensaje}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#0E3A53]">Asociar Mensaje a Reserva</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Selecciona una reserva</Label>
              <Select 
                value={reservaSeleccionadaAsociar} 
                onValueChange={setReservaSeleccionadaAsociar}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar reserva..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__sin_asociar__">Sin asociar</SelectItem>
                  {reservasDisponibles.map((reserva) => (
                    <SelectItem key={reserva.id_reserva} value={reserva.id_reserva}>
                      {reserva.huesped_nombre} - {new Date(reserva.fecha_check_in + 'T12:00:00').toLocaleDateString('es-CL')} a {new Date(reserva.fecha_check_out + 'T12:00:00').toLocaleDateString('es-CL')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-gray-500 mt-2">
                Puedes asociar este mensaje a una reserva para tener un historial organizado.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button 
                variant="outline" 
                onClick={() => setModalAsociarMensaje(false)}
              >
                Cancelar
              </Button>
              <Button 
                onClick={asociarMensajeAReserva}
                className="bg-[#1AA1D8] hover:bg-[#0E3A53]"
              >
                Guardar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MensajesWhatsApp;
